import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Sparkles } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import { scGetSifDetail, scGetNavHistory, scFactsheetUrl } from "../../lib/sifcontrolApi";

const fmtNum = (v, unit = "", digits = 2) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (!Number.isFinite(n)) return "—";
    const s = n.toFixed(digits);
    if (unit === "%") return `${s}%`;
    if (unit === "days") return `${Math.round(n)} days`;
    return s;
};

const fmtRupees = (v) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

const KV = ({ label, value }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{label}</div>
        <div className="mt-1 text-sm font-body text-oth-ink">{value || "—"}</div>
    </div>
);

const MetricRow = ({ m }) => (
    <div className="grid grid-cols-[1fr_120px_120px] items-center px-4 py-2.5 border-b border-oth-line/60 last:border-b-0" data-testid={`sc-detail-metric-${m.metric_name}`}>
        <div className="text-sm font-body text-oth-ink">{m.label || m.metric_name}</div>
        <div className={`text-sm font-body text-right ${m.eligible ? "text-oth-ink" : "text-oth-ink3"}`}>
            {m.eligible ? fmtNum(m.value, m.unit) : "—"}
        </div>
        <div className="text-[11px] font-body text-right text-oth-ink3">{m.eligible ? `${m.history_days}d` : "needs more"}</div>
    </div>
);

const MetricSection = ({ title, metrics }) => {
    if (!metrics || metrics.length === 0) return null;
    return (
        <div className="rounded-2xl border border-oth-line bg-white overflow-hidden">
            <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body border-b border-oth-line bg-oth-bg-2/60">{title}</div>
            {metrics.map((m) => <MetricRow key={m.metric_name} m={m} />)}
        </div>
    );
};

const WhyBar = ({ score }) => {
    const w = Math.max(0, Math.min(100, score || 0));
    return (
        <div className="h-1.5 bg-oth-bg-2 rounded-full overflow-hidden">
            <div className="h-full bg-oth-ink rounded-full" style={{ width: `${w}%` }} />
        </div>
    );
};

const WhyRating = ({ r }) => (
    <div className="rounded-2xl border border-oth-line bg-white p-5" data-testid={`sc-detail-rating-${r.id}`}>
        <div className="flex items-start justify-between gap-3">
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Rating · {r.name}</div>
                <div className="mt-1 font-display text-2xl font-semibold tracking-tight text-oth-ink">{r.star_label}</div>
                {r.status === "ranked" && (
                    <div className="mt-0.5 text-[11px] font-body text-oth-ink3">Score {fmtNum(r.score, "", 1)} · Rank #{r.rank}</div>
                )}
            </div>
            <Sparkles size={14} className="text-oth-ink3" />
        </div>
        {r.status !== "ranked" ? (
            <div className="mt-4 text-[12px] font-body text-oth-ink3">{r.unranked_reason || "Insufficient data to rank against this methodology yet."}</div>
        ) : (
            <div className="mt-4 space-y-3">
                {(r.breakdown || []).map((b, i) => (
                    <div key={`${r.id}-${b.metric_name}-${i}`}>
                        <div className="flex items-center justify-between gap-3 text-xs font-body">
                            <div className="text-oth-ink">{b.label}</div>
                            <div className="text-oth-ink3">
                                {fmtNum(b.raw, b.unit)} <span className="text-oth-ink3">·</span> <span className="text-oth-ink">{fmtNum(b.score, "", 0)}</span>
                                <span className="text-oth-ink3"> × {fmtNum(b.effective_weight ?? b.weight, "%", 0)}</span>
                            </div>
                        </div>
                        <div className="mt-1"><WhyBar score={b.score} /></div>
                    </div>
                ))}
            </div>
        )}
    </div>
);

export default function SifControlSifDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [nav, setNav] = useState({ points: [] });
    const [win, setWin] = useState("si");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setLoading(true);
        scGetSifDetail(id)
            .then(setData)
            .catch((e) => setErr(e?.response?.data?.detail || "Not found"))
            .finally(() => setLoading(false));
    }, [id]);
    useEffect(() => {
        if (!id) return;
        scGetNavHistory(id, win).then(setNav).catch(() => setNav({ points: [] }));
    }, [id, win]);

    const groups = data?.metrics_by_category || {};
    const primaryRating = useMemo(() => (data?.ratings || []).find((r) => r.status === "ranked") || (data?.ratings || [])[0], [data]);

    if (loading) return <div className="p-16 text-center text-sm text-oth-ink3">Loading…</div>;
    if (err) return <div className="p-16 text-center text-sm text-oth-ink3" data-testid="sc-sif-error">SIF not found.</div>;

    const s = data.sif;
    const amc = data.amc || {};

    return (
        <section className="py-14 md:py-20" data-testid="sc-sif-detail">
            <div className="mx-auto max-w-[1240px] px-4 md:px-6">
                <Link to="/sifcontrol/sifs" className="inline-flex items-center gap-1.5 text-xs font-body text-oth-ink3 hover:text-oth-ink" data-testid="sc-sif-back">
                    <ArrowLeft size={13} /> All SIFs
                </Link>

                <div className="mt-4 flex flex-wrap items-baseline justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-oth-ink">{s.sif_name}</h1>
                        <div className="mt-1 text-sm font-body text-oth-ink2">{amc.amc_name || "—"} · {s.strategy_category} · {s.sif_code}</div>
                    </div>
                    <a
                        href={scFactsheetUrl(id)}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-pill btn-pill-dark !text-xs !py-2 !px-4"
                        data-testid="sc-sif-factsheet"
                    >
                        <Download size={13} /> Download factsheet
                    </a>
                </div>

                {/* Key stats */}
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 rounded-3xl border border-oth-line bg-white p-6 md:p-8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">Rating</div>
                        <div className="mt-1 font-display text-2xl font-semibold text-oth-ink" data-testid="sc-sif-rating">
                            {primaryRating?.star_label || "Unrated"}
                        </div>
                        <div className="text-[10px] font-body text-oth-ink3 mt-0.5">{primaryRating?.name || "Awaiting data"}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">AUM</div>
                        <div className="mt-1 font-display text-2xl font-semibold text-oth-ink" data-testid="sc-sif-aum">
                            {data.aum_latest ? `${Number(data.aum_latest.aum).toFixed(0)} Cr` : "—"}
                        </div>
                        <div className="text-[10px] font-body text-oth-ink3 mt-0.5">{data.aum_latest?.period || " "}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">NAV</div>
                        <div className="mt-1 font-display text-2xl font-semibold text-oth-ink" data-testid="sc-sif-nav">
                            {data.nav_latest ? Number(data.nav_latest.nav).toFixed(4) : "—"}
                        </div>
                        <div className="text-[10px] font-body text-oth-ink3 mt-0.5">{data.nav_latest?.nav_date || " "}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">TER</div>
                        <div className="mt-1 font-display text-2xl font-semibold text-oth-ink" data-testid="sc-sif-ter">
                            {data.ter_latest ? `${Number(data.ter_latest.ter).toFixed(2)}%` : "—"}
                        </div>
                        <div className="text-[10px] font-body text-oth-ink3 mt-0.5">{data.ter_latest?.period || " "}</div>
                    </div>
                </div>

                {/* Chart */}
                <div className="mt-8 rounded-2xl border border-oth-line bg-white p-4 md:p-5" data-testid="sc-sif-chart">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-oth-ink3 font-body">NAV history</div>
                        <div className="flex items-center gap-1">
                            {["1y", "3y", "si"].map((w) => (
                                <button key={w} onClick={() => setWin(w)} className={`px-3 py-1 rounded-full text-[11px] font-body ${win === w ? "bg-oth-ink text-white" : "text-oth-ink3 hover:bg-oth-bg-2"}`} data-testid={`sc-sif-chart-${w}`}>
                                    {w.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="h-56">
                        {nav.points && nav.points.length >= 2 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={nav.points.map((p) => ({ date: p.nav_date, nav: Number(p.nav) }))}>
                                    <CartesianGrid stroke="#EEE9E1" strokeDasharray="2 2" vertical={false} />
                                    <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "#8C877E" }} minTickGap={40} />
                                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "#8C877E" }} domain={["auto", "auto"]} width={38} />
                                    <Tooltip contentStyle={{ fontFamily: "Inter Tight", fontSize: 11, background: "#fff", border: "1px solid #D9D7D1", borderRadius: 12 }} />
                                    <Line type="monotone" dataKey="nav" stroke="#0A0A0A" strokeWidth={1.6} dot={false} />
                                    <ReferenceLine y={nav.points[0]?.nav} stroke="#D9D7D1" strokeDasharray="3 3" />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full flex items-center justify-center text-xs font-body text-oth-ink3" data-testid="sc-sif-chart-empty">
                                Need at least 2 NAV observations to plot. Daily AMFI ingestion will populate this over time.
                            </div>
                        )}
                    </div>
                </div>

                {/* Overview */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 rounded-3xl border border-oth-line bg-white p-6 md:p-8">
                    <KV label="Fund manager" value={s.fund_manager} />
                    <KV label="Co-fund manager" value={s.co_fund_manager} />
                    <KV label="Benchmark" value={s.benchmark} />
                    <KV label="Launch date" value={s.launch_date} />
                    <KV label="Minimum investment" value={s.minimum_investment ? fmtRupees(s.minimum_investment) : ""} />
                    <KV label="Exit load" value={s.exit_load} />
                    <KV label="Plan / Option" value={`${s.plan || "—"} · ${s.option || "—"}`} />
                    <KV label="Investment objective" value={s.investment_objective} />
                </div>

                {/* Metric sections */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <MetricSection title="Returns" metrics={groups.returns} />
                    <MetricSection title="Risk" metrics={groups.risk} />
                    <MetricSection title="Risk-adjusted" metrics={groups.risk_adjusted} />
                    <MetricSection title="Consistency" metrics={groups.consistency} />
                </div>

                {/* Why this rating? — one card per active methodology */}
                {(data.ratings || []).length > 0 && (
                    <div className="mt-10">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body mb-3">Why this rating?</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.ratings.map((r) => <WhyRating key={r.id} r={r} />)}
                        </div>
                    </div>
                )}

                <div className="mt-8 text-[11px] font-body text-oth-ink3">
                    Data sources — AMFI (NAV, AUM, TER) · AMC (fund manager, strategy, factsheet) · SIF Control (all calculated analytics + ratings).
                </div>
            </div>
        </section>
    );
}
